import {
  Box,
  Typography,
  TextField,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  SelectChangeEvent,
} from "@mui/material";
import {
  CheckIcon,
  PencilSimpleIcon,
  ImageIcon,
  TrashSimpleIcon,
  XIcon,
} from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { useArt } from "../../stores/useArt";
import { useProfile } from "../../stores/useProfile";
import { Art } from "../../Models/Art";
import { ArtDto } from "../../Models/ArtDto";

type ArtTableRowProps = {
  artItem: Art;
  onSave: () => void;
  onImageClick: (artItemId: number) => void;
};

export const ArtTableRow = ({
  artItem,
  onSave,
  onImageClick,
}: ArtTableRowProps) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [confirmation, setConfirmation] = useState<boolean>(false);
  const [editedValues, setEditedValues] = useState<Partial<ArtDto>>({});
  const { editArt, removeArt } = useArt();
  const { user } = useProfile();

  useEffect(() => {
    updateEditedValues();
    // eslint-disable-next-line
  }, []);

  const updateEditedValues = () => {
    setEditedValues({
      title: artItem.title,
      description: artItem.description,
      sellState: artItem.sellState,
      type: artItem.type,
      viewCount: artItem.viewCount,
      creationDate: artItem.creationDate,
    });
  };

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent
  ) => {
    const { name, value } = e.target as HTMLInputElement;
    setEditedValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!user) return;
    if (isEditing) await editArt(user, editedValues, artItem.id!);
    else await removeArt(user, artItem.id!);
    setIsEditing(false);
    setConfirmation(false);
    onSave();
  };

  const handleAction = (artItemId: number, action: string) => {
    setIsEditing(false);
    setConfirmation(false);
    updateEditedValues();
    if (action === "edit") setIsEditing(true);
    if (action === "remove") setConfirmation(true);
  };

  return (
    <Box key={artItem.id} className="artItem">
      {isEditing ? (
        <>
          <Typography variant="h4" className="id">
            {artItem.id}
          </Typography>
          <TextField
            value={editedValues.title}
            className="title"
            id="title"
            name="title"
            onChange={handleChange}
          />
          <TextField
            value={editedValues.description}
            className="description"
            id="description"
            name="description"
            onChange={handleChange}
          />
          <Box className="type">
            <FormControl fullWidth>
              <Select
                labelId="type-label"
                value={editedValues.type}
                name="type"
                label="Art Type"
                onChange={handleChange}>
                <MenuItem value="PAINTING">Painting</MenuItem>
                <MenuItem value="SCULPTURE">Sculpture</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box className="sellstate">
            <FormControl fullWidth>
              <Select
                labelId="type-label"
                name="sellState"
                label="Art Type"
                value={editedValues.sellState}
                onChange={handleChange}>
                <MenuItem value="Te Koop">Te Koop</MenuItem>
                <MenuItem value="Verkocht">Verkocht</MenuItem>
                <MenuItem value="Verkocht in opdracht">
                  Verkocht in opdracht
                </MenuItem>
                <MenuItem value="Verkocht / Op bestelling verkrijgbaar">
                  Verkocht / Op bestelling verkrijgbaar
                </MenuItem>
                <MenuItem value="Te koop in Atelier">
                  Te koop in Atelier
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Typography variant="h4" className="vieuws">
            {artItem.viewCount}
          </Typography>
          <Typography variant="h4" className="date">
            {new Date(artItem.creationDate).toLocaleDateString("en-GB")}
          </Typography>
          <Box className="actions">
            <IconButton onClick={handleSave}>
              <CheckIcon size={24} />
            </IconButton>
            <IconButton onClick={() => handleAction(artItem.id!, "close")}>
              <XIcon size={24} />
            </IconButton>
          </Box>
        </>
      ) : (
        <>
          <Typography variant="h4" className="id">
            {artItem.id}
          </Typography>
          <Typography variant="h4" className="title">
            {artItem.title}
          </Typography>
          <Typography variant="h4" className="description">
            {artItem.description}
          </Typography>
          <Typography variant="h4" className="type">
            {artItem.type}
          </Typography>
          <Typography variant="h4" className="sellstate">
            {artItem.sellState}
          </Typography>
          <Typography variant="h4" className="vieuws">
            {artItem.viewCount}
          </Typography>
          <Typography variant="h4" className="date">
            {new Date(artItem.creationDate).toLocaleDateString("en-GB")}
          </Typography>
          <Box className="actions">
            {confirmation ? (
              <>
                <IconButton onClick={handleSave}>
                  <CheckIcon size={24} />
                </IconButton>
                <IconButton onClick={() => handleAction(artItem.id!, "close")}>
                  <XIcon size={24} />
                </IconButton>
              </>
            ) : (
              <>
                <IconButton onClick={() => handleAction(artItem.id!, "edit")}>
                  <PencilSimpleIcon size={24} weight="bold" />
                </IconButton>
                <IconButton onClick={() => onImageClick(artItem.id!)}>
                  <ImageIcon size={24} weight="bold" />
                </IconButton>
                <IconButton onClick={() => handleAction(artItem.id!, "remove")}>
                  <TrashSimpleIcon size={24} weight="bold" />
                </IconButton>
              </>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};
